import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { io, type Socket } from 'socket.io-client';
import type {
  AnnouncementView,
  ClientToServerEvents,
  Facing,
  KnockView,
  ManualStatus,
  NotificationView,
  PresenceUser,
  ServerToClientEvents,
  TimerSnapshot,
} from '@homework/shared';
import { MOVE_FLUSH_MS, SOCKET_PATH } from '@homework/shared';
import { api, getAccessToken, onAccessTokenChange } from './api';
import { useAuth } from './auth';
import { useToast } from './toast';

export type AppClientSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

interface RealtimeContextValue {
  socket: AppClientSocket | null;
  connected: boolean;
  /** Everyone currently in the office, keyed by user id. */
  presence: Map<string, PresenceUser>;
  timer: TimerSnapshot | null;
  knocks: KnockView[];
  notifications: NotificationView[];
  latestAnnouncement: AnnouncementView | null;
  setStatus: (status: ManualStatus, statusMessage?: string | null) => Promise<void>;
  move: (x: number, y: number, facing: Facing) => void;
  knock: (targetUserId: string, intent: 'CHAT' | 'CALL', reason?: string) => Promise<{ ok: boolean; knockId?: string; error?: string }>;
  answerKnock: (knockId: string, accept: boolean) => void;
  dismissNotification: (id: string) => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { status: authStatus, me } = useAuth();
  const { push } = useToast();
  const [socket, setSocket] = useState<AppClientSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [presence, setPresence] = useState<Map<string, PresenceUser>>(new Map());
  const [timer, setTimer] = useState<TimerSnapshot | null>(null);
  const [knocks, setKnocks] = useState<KnockView[]>([]);
  const [notifications, setNotifications] = useState<NotificationView[]>([]);
  const [latestAnnouncement, setLatestAnnouncement] = useState<AnnouncementView | null>(null);
  const pushRef = useRef(push);
  pushRef.current = push;

  // ---- connection lifecycle
  useEffect(() => {
    if (authStatus !== 'authenticated') {
      setSocket(null);
      setConnected(false);
      setPresence(new Map());
      return;
    }

    const client: AppClientSocket = io({
      path: SOCKET_PATH,
      transports: ['websocket', 'polling'],
      withCredentials: true,
      auth: { token: getAccessToken() },
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
    });

    // Reconnect attempts must carry a fresh token, or a rotated JWT locks the
    // socket out until a full reload.
    const stopTokenWatch = onAccessTokenChange((token) => {
      client.auth = { token };
    });
    client.io.on('reconnect_attempt', () => {
      client.auth = { token: getAccessToken() };
    });

    client.on('connect', () => setConnected(true));
    client.on('disconnect', () => setConnected(false));
    client.on('connect_error', (error) => {
      setConnected(false);
      // An expired token is the common case; ask for a new one and let
      // socket.io retry with it.
      if (error.message === 'unauthorized') {
        void api.post<{ accessToken: string }>('/auth/socket-token').catch(() => undefined);
      }
    });

    client.on('presence:snapshot', ({ users }) => {
      setPresence(new Map(users.map((user) => [user.userId, user])));
    });
    client.on('presence:update', ({ user }) => {
      setPresence((previous) => new Map(previous).set(user.userId, user));
    });
    client.on('presence:offline', ({ userId }) => {
      setPresence((previous) => {
        const next = new Map(previous);
        next.delete(userId);
        return next;
      });
    });
    client.on('map:positions', ({ positions }) => {
      setPresence((previous) => {
        const next = new Map(previous);
        for (const position of positions) {
          const user = next.get(position.userId);
          if (user) {
            next.set(position.userId, {
              ...user,
              position: { x: position.x, y: position.y, facing: position.facing },
            });
          }
        }
        return next;
      });
    });
    client.on('timer:tick', (snapshot) => setTimer(snapshot));

    client.on('knock:incoming', ({ knock }) => {
      setKnocks((previous) => [knock, ...previous.filter((entry) => entry.id !== knock.id)]);
    });
    client.on('knock:resolved', ({ knockId }) => {
      setKnocks((previous) => previous.filter((entry) => entry.id !== knockId));
    });

    client.on('announcement:new', ({ announcement }) => {
      setLatestAnnouncement(announcement);
      pushRef.current({
        kind: 'announcement',
        title: announcement.title,
        body: announcement.body,
        duration: 0,
      });
    });

    client.on('notification:new', ({ notification }) => {
      setNotifications((previous) => [notification, ...previous].slice(0, 50));
      pushRef.current({
        kind: notification.kind === 'INTEGRATION_ERROR' ? 'error' : 'info',
        title: notification.title,
        body: notification.body,
      });
    });

    client.on('server:error', ({ message }) => {
      pushRef.current({ kind: 'error', title: 'Something went wrong', body: message });
    });

    setSocket(client);
    return () => {
      stopTokenWatch();
      client.removeAllListeners();
      client.disconnect();
    };
  }, [authStatus]);

  // ---- pending knocks and notifications survive a reload
  useEffect(() => {
    if (authStatus !== 'authenticated') return;
    void api
      .get<{ items: KnockView[] }>('/me/knocks')
      .then((result) => setKnocks(result.items))
      .catch(() => undefined);
    void api
      .get<{ items: NotificationView[] }>('/me/notifications')
      .then((result) => setNotifications(result.items))
      .catch(() => undefined);
  }, [authStatus]);

  /**
   * FR-3.4 — the server needs to know the person is still there. Real input
   * events are throttled to one heartbeat a minute; the socket's own ping is
   * not evidence of activity, since an idle tab keeps pinging.
   */
  useEffect(() => {
    if (!socket) return;
    let last = 0;
    const beat = () => {
      const now = Date.now();
      if (now - last < 60_000) return;
      last = now;
      socket.emit('presence:activity');
    };
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
    for (const event of events) window.addEventListener(event, beat, { passive: true });
    const onVisible = () => {
      if (document.visibilityState === 'visible') beat();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      for (const event of events) window.removeEventListener(event, beat);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [socket]);

  const setStatus = useCallback(
    async (status: ManualStatus, statusMessage?: string | null) => {
      if (!socket) {
        await api.patch('/me/status', { status, statusMessage });
        return;
      }
      await new Promise<void>((resolve, reject) => {
        socket.emit('presence:setStatus', { status, statusMessage }, (result) => {
          if (result?.ok) resolve();
          else reject(new Error(result?.error ?? 'Could not update status'));
        });
      });
    },
    [socket],
  );

  // Movement is flushed on a fixed interval rather than per keypress.
  const pendingMove = useRef<{ x: number; y: number; facing: Facing } | null>(null);
  useEffect(() => {
    if (!socket) return;
    const interval = setInterval(() => {
      const move = pendingMove.current;
      if (!move) return;
      pendingMove.current = null;
      socket.emit('map:move', move);
    }, MOVE_FLUSH_MS);
    return () => clearInterval(interval);
  }, [socket]);

  const move = useCallback((x: number, y: number, facing: Facing) => {
    pendingMove.current = { x, y, facing };
  }, []);

  const knock = useCallback(
    (targetUserId: string, intent: 'CHAT' | 'CALL', reason?: string) =>
      new Promise<{ ok: boolean; knockId?: string; error?: string }>((resolve) => {
        if (!socket) {
          resolve({ ok: false, error: 'Not connected' });
          return;
        }
        socket.emit('knock:request', { targetUserId, intent, reason }, (result) =>
          resolve(result ?? { ok: false, error: 'No response' }),
        );
      }),
    [socket],
  );

  const answerKnock = useCallback(
    (knockId: string, accept: boolean) => {
      socket?.emit('knock:respond', { knockId, accept });
      setKnocks((previous) => previous.filter((entry) => entry.id !== knockId));
    },
    [socket],
  );

  const dismissNotification = useCallback((id: string) => {
    setNotifications((previous) => previous.filter((entry) => entry.id !== id));
    void api.post('/me/notifications/read', { ids: [id] }).catch(() => undefined);
  }, []);

  // Keep our own presence card in sync even before the server echoes it back.
  useEffect(() => {
    if (!me || !timer) return;
    setPresence((previous) => {
      const mine = previous.get(me.user.id);
      if (!mine || mine.status === timer.status) return previous;
      return new Map(previous).set(me.user.id, { ...mine, status: timer.status });
    });
  }, [me, timer]);

  const value = useMemo<RealtimeContextValue>(
    () => ({
      socket,
      connected,
      presence,
      timer,
      knocks,
      notifications,
      latestAnnouncement,
      setStatus,
      move,
      knock,
      answerKnock,
      dismissNotification,
    }),
    [
      socket,
      connected,
      presence,
      timer,
      knocks,
      notifications,
      latestAnnouncement,
      setStatus,
      move,
      knock,
      answerKnock,
      dismissNotification,
    ],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeContextValue {
  const context = useContext(RealtimeContext);
  if (!context) throw new Error('useRealtime must be used inside <RealtimeProvider>');
  return context;
}

/**
 * Subscribes to one server event for the lifetime of a component. Feature
 * modules (chat, calls) use this instead of touching the socket directly, so
 * listeners are always cleaned up.
 */
export function useSocketEvent<E extends keyof ServerToClientEvents>(
  event: E,
  handler: ServerToClientEvents[E],
): void {
  const { socket } = useRealtime();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!socket) return;
    const listener = (...args: unknown[]) => {
      (handlerRef.current as (...inner: unknown[]) => void)(...args);
    };
    // socket.io's per-event listener types cannot be satisfied while `event` is
    // still generic, so the registration is narrowed here. The public signature
    // above keeps the handler type-checked against the shared contract.
    const emitter = socket as unknown as {
      on: (name: string, fn: (...args: unknown[]) => void) => void;
      off: (name: string, fn: (...args: unknown[]) => void) => void;
    };
    emitter.on(event, listener);
    return () => {
      emitter.off(event, listener);
    };
  }, [socket, event]);
}
